import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { Text } from "@chakra-ui/react";

interface KYDLoadingProps {
  strokeColor?: string;
  backgroundColor?: string;
  size?: number;
  withText?: string;
}

export default function KYDLoading({
  strokeColor = "#ffffff",
  backgroundColor = "transparent",
  size = 32,
  withText = "",
}: KYDLoadingProps) {
  const [key, setKey] = useState(0);

  const unfoldDuration = 0.4;
  const holdDuration = 0.4;
  const reverseDuration = 0.3;
  const enableReverse = true;
  const reverseOffset1 = 0.85;
  const reverseOffset2 = 0.85;
  const reverseOffset3 = 0.85;
  const reverseOffset4 = 0.85;
  const reverseOffset5 = 0.85;
  const height = (size * 14) / 22;

  const totalDuration =
    unfoldDuration + holdDuration + (enableReverse ? reverseDuration : 0) + 0.1;

  useEffect(() => {
    const timer = setInterval(() => {
      setKey((prev) => prev + 1);
    }, totalDuration * 1000);

    return () => clearInterval(timer);
  }, [totalDuration]);

  const unfoldEnd = unfoldDuration / totalDuration;
  const holdEnd = (unfoldDuration + holdDuration) / totalDuration;

  return (
    <>
      <div
        className="relative"
        style={{ width: `${size}px`, height: `${height}px`, backgroundColor }}
      >
        <motion.div key={key}>
          <svg
            className="block"
            width={size}
            height={height}
            fill="none"
            viewBox="0 0 22 14"
          >
            <motion.line
              stroke={strokeColor}
              strokeWidth="3"
              x1="0"
              x2="11"
              y1="12.5"
              y2="12.5"
              initial={{ pathLength: 0 }}
              animate={{
                pathLength: enableReverse ? [0, 1, 1, 1, 0] : [0, 1, 1, 1],
              }}
              transition={{
                duration: totalDuration,
                times: enableReverse
                  ? [
                      0,
                      (unfoldDuration * 0.2) / totalDuration,
                      unfoldEnd,
                      holdEnd,
                      holdEnd +
                        (reverseDuration * reverseOffset1) / totalDuration,
                    ]
                  : [0, (unfoldDuration * 0.2) / totalDuration, unfoldEnd, 1],
                ease: "easeInOut",
              }}
            />
            <motion.line
              stroke={strokeColor}
              strokeWidth="3"
              x1="2.061"
              x2="9.839"
              y1="3.939"
              y2="11.718"
              initial={{ pathLength: 0 }}
              animate={{
                pathLength: enableReverse
                  ? [0, 0, 1, 1, 1, 0]
                  : [0, 0, 1, 1, 1],
              }}
              transition={{
                duration: totalDuration,
                times: enableReverse
                  ? [
                      0,
                      (unfoldDuration * 0.2) / totalDuration,
                      (unfoldDuration * 0.4) / totalDuration,
                      unfoldEnd,
                      holdEnd,
                      holdEnd +
                        (reverseDuration * reverseOffset2) / totalDuration,
                    ]
                  : [
                      0,
                      (unfoldDuration * 0.2) / totalDuration,
                      (unfoldDuration * 0.4) / totalDuration,
                      unfoldEnd,
                      1,
                    ],
                ease: "easeInOut",
              }}
            />
            <motion.line
              stroke={strokeColor}
              strokeWidth="3"
              x1="10.5"
              x2="10.5"
              y1="0"
              y2="11"
              initial={{ pathLength: 0 }}
              animate={{
                pathLength: enableReverse
                  ? [0, 0, 1, 1, 1, 0]
                  : [0, 0, 1, 1, 1],
              }}
              transition={{
                duration: totalDuration,
                times: enableReverse
                  ? [
                      0,
                      (unfoldDuration * 0.4) / totalDuration,
                      (unfoldDuration * 0.6) / totalDuration,
                      unfoldEnd,
                      holdEnd,
                      holdEnd +
                        (reverseDuration * reverseOffset3) / totalDuration,
                    ]
                  : [
                      0,
                      (unfoldDuration * 0.4) / totalDuration,
                      (unfoldDuration * 0.6) / totalDuration,
                      unfoldEnd,
                      1,
                    ],
                ease: "easeInOut",
              }}
            />
            <motion.line
              stroke={strokeColor}
              strokeWidth="3"
              x1="18.839"
              x2="11.061"
              y1="4.061"
              y2="11.839"
              initial={{ pathLength: 0 }}
              animate={{
                pathLength: enableReverse
                  ? [0, 0, 1, 1, 1, 0]
                  : [0, 0, 1, 1, 1],
              }}
              transition={{
                duration: totalDuration,
                times: enableReverse
                  ? [
                      0,
                      (unfoldDuration * 0.6) / totalDuration,
                      (unfoldDuration * 0.8) / totalDuration,
                      unfoldEnd,
                      holdEnd,
                      holdEnd +
                        (reverseDuration * reverseOffset4) / totalDuration,
                    ]
                  : [
                      0,
                      (unfoldDuration * 0.6) / totalDuration,
                      (unfoldDuration * 0.8) / totalDuration,
                      unfoldEnd,
                      1,
                    ],
                ease: "easeInOut",
              }}
            />
            <motion.line
              stroke={strokeColor}
              strokeWidth="3"
              x1="22"
              x2="11"
              y1="12.5"
              y2="12.5"
              initial={{ pathLength: 0 }}
              animate={{
                pathLength: enableReverse
                  ? [0, 0, 1, 1, 1, 0]
                  : [0, 0, 1, 1, 1],
              }}
              transition={{
                duration: totalDuration,
                times: enableReverse
                  ? [
                      0,
                      (unfoldDuration * 0.8) / totalDuration,
                      unfoldEnd,
                      unfoldEnd,
                      holdEnd,
                      holdEnd +
                        (reverseDuration * reverseOffset5) / totalDuration,
                    ]
                  : [
                      0,
                      (unfoldDuration * 0.8) / totalDuration,
                      unfoldEnd,
                      unfoldEnd,
                      1,
                    ],
                ease: "easeInOut",
              }}
            />
          </svg>
        </motion.div>
      </div>
      {withText && (
        <Text
          fontSize={`${size / 4}px`}
          color={`${strokeColor}`}
          mt="2"
          fontFamily="IBM Plex Mono, Courier New, monospace"
          fontWeight="700"
          textTransform="uppercase"
          letterSpacing="0.2em"
        >
          {withText}
        </Text>
      )}
    </>
  );
}
